import { useEffect, useState } from 'react';
import { useList, useTranslate } from '@refinedev/core';
import { Button, Flex, InputNumber, Modal, Typography, theme } from 'antd';
import { FileSyncOutlined } from '@ant-design/icons';
import type { PublicationListOut } from '@/api/generated/schemas';
import { SyncReviewWizardModal } from './SyncReviewWizardModal';
import { MAX_SCAN_RANGE, scanQueue, scanRangeLength } from './syncReview';

const { Text } = Typography;

interface CatalogSyncReviewControlProps {
  /** At least one refresh landed; data should be refetched. Fires once, as the modal closes. */
  onDone?: () => void;
}

/**
 * "Synchronize the entire catalog": the sync-review wizard over a blind id
 * range, 1 to a chosen max by default, probing every id one request at a
 * time — an id with no publication behind it is checked and stepped over
 * (see `SyncReviewWizardModal`'s scan mode).
 */
export const CatalogSyncReviewControl = ({
  onDone,
}: CatalogSyncReviewControlProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const [setupOpen, setSetupOpen] = useState(false);
  const [from, setFrom] = useState<number>(1);
  const [to, setTo] = useState<number | null>(null);
  const [queue, setQueue] = useState<number[] | null>(null);

  const tk = (suffix: string, options?: Record<string, unknown>) =>
    t(`user_catalog.publications.sync_review.${suffix}`, options);

  // The highest publication id, to prefill the range's upper bound with
  // "the whole catalog as of now".
  const { result: maxIdPage } = useList<PublicationListOut>({
    resource: 'user_catalog.publications',
    pagination: { pageSize: 1 },
    sorters: [{ field: 'id', order: 'desc' }],
    queryOptions: { enabled: setupOpen && to == null },
  });
  const maxId = maxIdPage?.data?.[0]?.id;
  useEffect(() => {
    if (setupOpen && to == null && maxId != null) setTo(maxId);
  }, [setupOpen, to, maxId]);

  // Only the length during setup — the id array itself is as long as the
  // range and is built once, when the walk starts.
  const rangeLength = scanRangeLength(from, to ?? 0);
  const rangeTooLarge = rangeLength > MAX_SCAN_RANGE;

  const start = () => {
    setSetupOpen(false);
    setQueue(scanQueue(from, to ?? 0));
  };

  return (
    <>
      <Button icon={<FileSyncOutlined />} onClick={() => setSetupOpen(true)}>
        {tk('catalog_button')}
      </Button>

      <Modal
        open={setupOpen}
        title={tk('setup_title')}
        onCancel={() => setSetupOpen(false)}
        destroyOnClose
        footer={
          <Flex align="center" gap={token.marginSM}>
            <Text
              type="secondary"
              style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
            >
              {rangeLength > 0 &&
                !rangeTooLarge &&
                tk('requests', { count: rangeLength })}
            </Text>
            <Button onClick={() => setSetupOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              type="primary"
              disabled={rangeLength === 0 || rangeTooLarge}
              onClick={start}
            >
              {tk('start')}
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap={token.marginSM}>
          <Text type="secondary">{tk('setup_hint')}</Text>
          {rangeTooLarge && (
            <Text type="warning">
              {tk('range_too_large', { max: MAX_SCAN_RANGE })}
            </Text>
          )}
          <Flex gap={token.marginSM} align="center" wrap>
            <Flex vertical>
              <Text>{tk('from')}</Text>
              <InputNumber
                min={1}
                value={from}
                onChange={(value) => setFrom(value ?? 1)}
              />
            </Flex>
            <Flex vertical>
              <Text>{tk('to')}</Text>
              <InputNumber
                min={1}
                value={to}
                onChange={(value) => setTo(value)}
              />
            </Flex>
          </Flex>
        </Flex>
      </Modal>

      {queue && (
        <SyncReviewWizardModal
          queue={queue}
          scan
          onDone={onDone}
          onClose={() => setQueue(null)}
        />
      )}
    </>
  );
};
